// Error classes for the volunteer registration submit flow. Kept in a leaf
// module (no hook/SDK imports) so both the hook and the error-mapping logic can
// import them without a circular dependency.

/**
 * Thrown when a selected seat was booked by someone else or no longer exists,
 * but its group may still have room. Recoverable: the UI refreshes slots,
 * remaps to another seat on retry, and navigates back to the shifts panel.
 */
export class SlotConflictError extends Error {
  /** Raw error strings returned from the API. */
  readonly slotErrors: string[];

  constructor(slotErrors: string[]) {
    super('One or more volunteer slots are no longer available.');
    this.name = 'SlotConflictError';
    this.slotErrors = slotErrors;
  }
}

/**
 * Thrown when a shift filled up during registration: every seat in the group
 * is claimed, so there's no slot left to assign. Not a duplicate (the
 * volunteer never held a seat) and not remappable: the UI navigates back to
 * the shifts panel and asks the user to remove someone or pick another shift.
 */
export class ShiftFullError extends Error {
  /** Raw error strings returned from the API. */
  readonly slotErrors: string[];

  /**
   * Group keys (`name__start__end`) of the shifts that are full — every seat is
   * claimed once backend-rejected seats are excluded, so no remap is possible.
   * The UI highlights these specific shifts so the user knows exactly which
   * ones to change.
   */
  readonly fullGroupKeys: string[];

  constructor(slotErrors: string[], fullGroupKeys: string[] = []) {
    super('One or more selected shifts are now full.');
    this.name = 'ShiftFullError';
    this.slotErrors = slotErrors;
    this.fullGroupKeys = fullGroupKeys;
  }
}

/**
 * Thrown when the whole event has no seats left — not just the selected shift.
 * The UI shows the event-full alert and closes rather than bouncing to shifts.
 */
export class EventFullError extends Error {
  /** Raw error strings returned from the API. */
  readonly slotErrors: string[];

  constructor(slotErrors: string[]) {
    super('This event is now full.');
    this.name = 'EventFullError';
    this.slotErrors = slotErrors;
  }
}

/**
 * Whether an error is an EventFullError. Matches on `name`, not `instanceof` —
 * the error crosses a module boundary and `instanceof` can miss under HMR.
 */
export const isEventFullError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'EventFullError';

/**
 * Base for conflicts the flow remediates by removing the seat and locking the
 * shift. The backend may name the contact(s), so the UI can message precisely.
 */
export class RemediableConflictError extends Error {
  /** Raw error strings returned from the API. */
  readonly slotErrors: string[];

  /**
   * Display names of contacts auto-removed because the backend mapped them.
   * Empty when no conflict could be mapped to a removable assignment.
   */
  readonly removedContactNames: string[];

  /**
   * Display names the backend flagged but whose assignment couldn't be mapped
   * to a group (e.g. unmapped guest). The UI names them and asks the user to
   * double-check.
   */
  readonly unresolvedContactNames: string[];

  constructor(
    message: string,
    slotErrors: string[],
    removedContactNames: string[] = [],
    unresolvedContactNames: string[] = [],
  ) {
    super(message);
    this.slotErrors = slotErrors;
    this.removedContactNames = removedContactNames;
    this.unresolvedContactNames = unresolvedContactNames;
  }
}

/**
 * Thrown when a volunteer is already signed up for the selected shift (e.g.
 * booked in another tab). The duplicate can't be remapped, so the UI navigates
 * back to the shifts panel. Mapped contacts are removed and reported in
 * `removedContactNames` (review); unmapped ones land in `unresolvedContactNames`
 * (double-check).
 */
export class DuplicateInShiftError extends RemediableConflictError {
  /**
   * True when the same response also pruned one or more seats because their
   * shift group filled up. The duplicate outranks the full shift for the
   * primary message, but the removal still happened, so the UI surfaces it as
   * an additional error rather than swallowing it.
   */
  readonly hadFullShiftRemoval: boolean;

  constructor(
    slotErrors: string[],
    removedContactNames: string[] = [],
    unresolvedContactNames: string[] = [],
    hadFullShiftRemoval = false,
  ) {
    super(
      'One or more volunteers are already signed up for this shift.',
      slotErrors,
      removedContactNames,
      unresolvedContactNames,
    );
    this.name = 'DuplicateInShiftError';
    this.hadFullShiftRemoval = hadFullShiftRemoval;
  }
}

/**
 * Thrown when a volunteer is already committed to another event on the same
 * day. The conflict spans the whole day rather than a single seat, so it can't
 * be remapped: the seat is dropped and the shift is locked (they can't
 * register), and the UI navigates back to the shifts panel.
 */
export class SameDayConflictError extends RemediableConflictError {
  constructor(
    slotErrors: string[],
    removedContactNames: string[] = [],
    unresolvedContactNames: string[] = [],
  ) {
    super(
      'One or more volunteers are already booked elsewhere on that day.',
      slotErrors,
      removedContactNames,
      unresolvedContactNames,
    );
    this.name = 'SameDayConflictError';
  }
}

/**
 * Thrown on a 401: the supporter's session expired. Not a slot problem and not
 * retryable (auth isn't refreshed), so the UI prompts a re-login.
 */
export class SessionExpiredError extends Error {
  constructor() {
    super('Your session expired. Please log in again.');
    this.name = 'SessionExpiredError';
  }
}

/**
 * Thrown when the server can't resolve a contact for a guest registrant. This
 * is a data problem, not a slot conflict: the UI navigates back to the shifts
 * panel and asks the user to review the guest's details.
 */
export class GuestUnresolvedError extends Error {
  /** Raw error strings returned from the API. */
  readonly slotErrors: string[];

  constructor(slotErrors: string[]) {
    super('Could not resolve a contact for one or more guests.');
    this.name = 'GuestUnresolvedError';
    this.slotErrors = slotErrors;
  }
}

/**
 * Wraps several blocking errors from a single response (e.g. a same-day
 * conflict for one contact and a duplicate for another). The UI maps each child
 * to its own form error so every distinct problem is surfaced, not just the
 * highest-priority one.
 */
export class AggregateConflictError extends Error {
  readonly errors: Error[];

  constructor(errors: Error[]) {
    super('Multiple volunteer registration conflicts.');
    this.name = 'AggregateConflictError';
    this.errors = errors;
  }
}
